/**
 * 1.5.0 shot detection, stream S2: Detect shots by hand (plan §4).
 *
 *  - DetectShotsButton: "Detect shots…" in the asset page's action bar (OverflowBar, low priority: it sits in More);
 *    processing.retry, video with a proxy. Opens DetectShotsDialog.
 *  - DetectShotsPanel: the Processing tab's line ("42 shots · detected with sensitivity 50", or none yet) with its
 *    Detect shots… button.
 *  - DetectShotsDialog: the three settings prefilled from the workspace's (useShotSettings), sent as overrides
 *    (useDetectShots); then the run's step (the Processing tab's Steps) and the result "42 shots · sensitivity 50".
 *  - DetectShotsSelectionButton: the Library selection bar's "Detect shots on {n}" with the workspace's settings,
 *    within BULK_ACTION_LIMIT.
 */
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2, ScanLine } from 'lucide-react';
import {
  BULK_ACTION_LIMIT, SHOT_LIMITS, SHOTS_RUN_KIND, tooManyAssetsMessage,
  type AssetDetail, type AssetSummary, type DetectShotsRequest, type ShotsSummary,
} from '@prism/contracts';
import { ApiError, api, errorMessage } from '../api/client';
import { useMe } from '../api/hooks';
import { useDetectShots, useShotSettings } from '../api/hooks/shots';
import { queryKeys } from '../api/queryKeys';
import { relative } from '../lib/format';
import { Steps } from '../pages/ProcessingTab';
import { sensitivityHint } from '../pages/admin/transcoding/ShotsCard';
import { Modal } from './Modal';
import { toast } from './Toasts';

/** "42 shots · sensitivity 50". */
export const shotsLine = (s: ShotsSummary) => `${s.count} shot${s.count === 1 ? '' : 's'} · sensitivity ${s.settings.sensitivity}`;

const activeRun = (asset: AssetDetail) => asset.runs.find((r) => r.status === 'queued' || r.status === 'running');

/** Why Detect shots can't run on the asset now, or null. Undefined: not offered at all (not video). */
export function detectShotsBlocked(asset: AssetDetail): string | null | undefined {
  if (asset.deleted || asset.mediaKind !== 'video') return undefined;
  if (asset.live && (asset.live.state === 'recording' || asset.live.state === 'finishing')) return 'The recording has not ended yet';
  if (!asset.representations.some((r) => r.kind === 'proxy')) return 'The asset has no proxy yet';
  const run = activeRun(asset);
  if (run) return run.kind === SHOTS_RUN_KIND ? 'Detect shots is running' : 'Processing is running';
  return null;
}

function useCanDetect() {
  return Boolean(useMe().data?.permissions.includes('processing.retry'));
}

export function DetectShotsButton({ asset }: { asset: AssetDetail }) {
  const can = useCanDetect();
  const [open, setOpen] = useState(false);
  const blocked = detectShotsBlocked(asset);
  if (!can || blocked === undefined) return null;
  return (
    <>
      <button className="btn sm" disabled={Boolean(blocked)} title={blocked ?? 'Find the cuts again and remake the scrub images and the Shots layer'} onClick={() => setOpen(true)} data-testid="detect-shots">
        <ScanLine size={14} /> <span className="btn-label">Detect shots…</span>
      </button>
      {open && <DetectShotsDialog asset={asset} onClose={() => setOpen(false)} />}
    </>
  );
}

export function DetectShotsPanel({ asset }: { asset: AssetDetail }) {
  const can = useCanDetect();
  const [open, setOpen] = useState(false);
  const blocked = detectShotsBlocked(asset);
  if (blocked === undefined) return null;
  return (
    <div className="shots-panel" data-testid="shots-panel">
      <ScanLine size={14} className="muted" />
      <span data-testid="shots-summary">
        {asset.shots ? <>{shotsLine(asset.shots)} <span className="faint">· detected {relative(asset.shots.detectedAt)}</span></> : <span className="muted">No shots detected yet</span>}
      </span>
      <span style={{ flex: 1 }} />
      {can && (
        <button className="btn sm" disabled={Boolean(blocked)} title={blocked ?? undefined} onClick={() => setOpen(true)} data-testid="detect-shots-panel">
          Detect shots…
        </button>
      )}
      {open && <DetectShotsDialog asset={asset} onClose={() => setOpen(false)} />}
    </div>
  );
}

type Draft = { sensitivity: number; minShotSeconds: number; keyframeIntervalSeconds: number };
const FIELDS = [
  { key: 'minShotSeconds', label: 'Minimum shot length (s)', step: 0.1, help: 'Cuts closer than this to the one before are ignored' },
  { key: 'keyframeIntervalSeconds', label: 'Keyframe interval inside long shots (s)', step: 1, help: 'Adds scrub images only, never shots' },
] as const;

export function DetectShotsDialog({ asset, onClose }: { asset: AssetDetail; onClose: () => void }) {
  const settings = useShotSettings();
  const detect = useDetectShots();
  const qc = useQueryClient();
  const [d, setD] = useState<Draft | null>(null);
  const [runId, setRunId] = useState<string | null>(null);
  const [startedAt, setStartedAt] = useState<number>(0);
  useEffect(() => {
    if (settings.data && !d) setD({ sensitivity: settings.data.sensitivity, minShotSeconds: settings.data.minShotSeconds, keyframeIntervalSeconds: settings.data.keyframeIntervalSeconds });
  }, [settings.data, d]);
  const run = runId ? asset.runs.find((r) => r.id === runId) : undefined;
  const finished = run && run.status !== 'queued' && run.status !== 'running';
  // Until the run is over, refresh the asset (its runs and the summary) every 2 s as well as on its events.
  useEffect(() => {
    if (!runId || finished) return;
    const t = window.setInterval(() => void qc.invalidateQueries({ queryKey: queryKeys.asset(asset.id) }), 2000);
    return () => window.clearInterval(t);
  }, [runId, finished, qc, asset.id]);
  const fresh = asset.shots && new Date(asset.shots.detectedAt).getTime() >= startedAt - 5000 ? asset.shots : null;
  const blocked = runId ? null : detectShotsBlocked(asset);
  const bad = !d || FIELDS.some((f) => Number.isNaN(d[f.key]));

  async function start() {
    if (!d || !settings.data) return;
    // Only what differs from the workspace's settings is sent as an override.
    const overrides: DetectShotsRequest = {};
    if (d.sensitivity !== settings.data.sensitivity) overrides.sensitivity = d.sensitivity;
    for (const f of FIELDS) {
      const lim = SHOT_LIMITS[f.key];
      const v = Math.min(lim.max, Math.max(lim.min, d[f.key]));
      if (v !== settings.data[f.key]) overrides[f.key] = v;
    }
    try {
      setStartedAt(Date.now());
      const r = await detect.mutateAsync({ assetId: asset.id, overrides });
      setRunId(r.runId);
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }

  return (
    <Modal title={`Detect shots · ${asset.title}`} onClose={onClose} actions={runId ? (
      <button className="btn primary" onClick={onClose} data-testid="detect-shots-close">{finished ? 'Done' : 'Close'}</button>
    ) : (
      <>
        <button className="btn ghost" onClick={onClose}>Cancel</button>
        <button className="btn primary" disabled={bad || Boolean(blocked) || detect.isPending} onClick={() => void start()} data-testid="detect-shots-run">
          {detect.isPending && <Loader2 size={14} className="spin" />} Run
        </button>
      </>
    )}>
      {!runId && (
        <>
          <p className="muted" style={{ margin: 0 }}>Finds the cuts in the proxy again, then remakes the scrub images and the read-only Shots layer. Log layers are left as they are.</p>
          {settings.isLoading && <div className="skeleton" style={{ height: 120 }} />}
          {settings.isError && <p className="banner error">{errorMessage(settings.error)}</p>}
          {d && (
            <div className="detect-shots-form">
              <div className="field">
                <label htmlFor="ds-sensitivity">Sensitivity <b className="mono" data-testid="ds-sensitivity-value">{d.sensitivity}</b></label>
                <input id="ds-sensitivity" type="range" min={SHOT_LIMITS.sensitivity.min} max={SHOT_LIMITS.sensitivity.max} step={1} value={d.sensitivity}
                  onChange={(e) => setD({ ...d, sensitivity: Number(e.target.value) })} data-testid="ds-sensitivity" />
                <span className="help">{sensitivityHint(d.sensitivity)}</span>
              </div>
              {FIELDS.map((f) => {
                const lim = SHOT_LIMITS[f.key];
                return (
                  <div className="field" key={f.key}>
                    <label htmlFor={`ds-${f.key}`}>{f.label}</label>
                    <input id={`ds-${f.key}`} className="input" type="number" min={lim.min} max={lim.max} step={f.step}
                      value={Number.isNaN(d[f.key]) ? '' : d[f.key]} data-testid={`ds-${f.key}`}
                      onChange={(e) => setD({ ...d, [f.key]: e.target.value.trim() === '' ? NaN : Number(e.target.value) })} />
                    <span className="help">{f.help} ({lim.min}–{lim.max}).</span>
                  </div>
                );
              })}
              <span className="help">Prefilled with the workspace’s settings (Administration → Transcoding).{asset.shots ? ` Now: ${shotsLine(asset.shots)}.` : ''}</span>
            </div>
          )}
          {blocked && <p className="banner warning" style={{ margin: 0 }}>{blocked}.</p>}
        </>
      )}
      {runId && (
        <div data-testid="detect-shots-progress">
          {run ? <Steps run={run} /> : <p className="muted"><Loader2 size={13} className="spin" /> Starting…</p>}
          {finished && run.status === 'succeeded' && fresh && <p className="detect-shots-result" data-testid="detect-shots-result">{shotsLine(fresh)}</p>}
          {finished && run.status !== 'succeeded' && <p className="banner error" data-testid="detect-shots-result">{run.error?.message ?? 'Detect shots did not finish.'}</p>}
          {!finished && <p className="faint" style={{ fontSize: 12 }}>You can close this; the run goes on and shows on the Processing tab.</p>}
        </div>
      )}
    </Modal>
  );
}

/** The selection bar's Detect shots: video with media, the workspace's settings, one request per asset. */
export function DetectShotsSelectionButton({ selected }: { selected: AssetSummary[] }) {
  const can = useCanDetect();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  if (!can) return null;
  const eligible = selected.filter((a) => a.mediaKind === 'video' && (a.status === 'ready' || a.status === 'partial') && !a.deleted);
  const n = eligible.length;
  const total = selected.length;
  const label = `Detect shots on ${n === total ? total : `${n} of ${total}`}`;
  const blocked = n === 0 ? 'None of the selected assets is a processed video' : n > BULK_ACTION_LIMIT ? `${tooManyAssetsMessage()} (${n} selected)` : null;
  const title = blocked ?? (n < total ? `${label} (${total - n} are not processed video)` : `${label} with the workspace’s settings`);

  async function run() {
    if (!window.confirm(`Detect shots on ${n} asset${n === 1 ? '' : 's'} with the workspace’s settings? Their scrub images and Shots layers are remade; log layers are left as they are.`)) return;
    setBusy(true);
    let started = 0;
    const skipped: string[] = [];
    for (const a of eligible) {
      try {
        await api(`/api/v1/assets/${a.id}/detect-shots`, { method: 'POST', json: {} });
        started++;
        void qc.invalidateQueries({ queryKey: queryKeys.asset(a.id) });
      } catch (err) {
        skipped.push(err instanceof ApiError && err.code === 'PROCESSING_ACTIVE' ? 'processing' : err instanceof ApiError && err.code === 'NO_PROXY' ? 'no proxy' : errorMessage(err));
      }
    }
    setBusy(false);
    const why = [...new Set(skipped)].join(', ');
    toast(`Detect shots started on ${started}${skipped.length ? `; ${skipped.length} skipped (${why})` : ''}`, started ? undefined : 'error');
  }

  return (
    <button className="btn ghost sm" disabled={busy || Boolean(blocked)} title={title} onClick={() => void run()} data-testid="selection-shots" data-eligible={n}>
      {busy ? <Loader2 size={13} className="spin" /> : <ScanLine size={13} />} {label}
    </button>
  );
}
